import { useDispatch } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from '../../utils/dayjs';
import { openSnackbar } from '../../components/AppSnackbar/snackbarSlice';
import { TrackerDataType } from '../constants/constants';
import { ContentLogKeys } from '../constants/IssueAndServiceKeys';
import {
  buildContentLogPayload,
  createContentLog,
  deleteContentLog,
  duplicateContentLog,
  updateContentLogWithFiles,
  uploadContentLogFiles,
  uploadContentLogUrl,
} from '../requests/contentLogsRequest';
import { RecentCreations } from '../hooks/useRecentCreations';
import { ContentLog, File as TrackerFile, FlatContentLog, UpdateContentLogWithFilesMutationProps } from '../types';

interface UseContentLogHandlerProps {
  recentCreations: RecentCreations;
  onSuccessCallback: () => Promise<void>;
}

interface CreateContentLogMutationFnProps {
  fields: ContentLog;
  files: Array<globalThis.File | (TrackerFile & { url?: string })>;
}

const mapExportValues = (values: any[] = [], getLabel: (value: any) => string) => {
  return values.map(getLabel).join(', ');
};

const getDropdownLabel = (options: any[] = [], value: any) => {
  if (value?.name) {
    return value.name;
  }

  return options.find((candidate: any) => candidate._id === value)?.name ?? value;
};

export const useContentLogHandler = ({ recentCreations, onSuccessCallback }: UseContentLogHandlerProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { markCreated, isNewlyAdded } = recentCreations;

  const invalidateLinkedServiceQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['serviceById'] });
  };

  const createContentLogMutation = useMutation({
    mutationFn: async ({ fields, files }: CreateContentLogMutationFnProps) => {
      const requestPayload = buildContentLogPayload(fields);
      const response = await createContentLog(requestPayload);
      if (response.status !== 201) {
        dispatch(openSnackbar({ severity: 'alert', message: response.statusText }));
        throw new Error(response.statusText);
      }

      const id = response.data.id || null;
      if (id) {
        const browserFiles = files.filter((file): file is globalThis.File => file instanceof globalThis.File);
        const uploadResp = await uploadContentLogFiles({ id, files: browserFiles });
        if (uploadResp.status !== 201) {
          dispatch(openSnackbar({ severity: 'warning', message: 'File upload failed for newly created content log' }));
        }

        await Promise.all(
          files
            .filter((file): file is TrackerFile & { url?: string } => 'type' in file && file.type === 'url')
            .map((file) =>
              uploadContentLogUrl({
                id,
                file: {
                  name: file.name,
                  description: file.description ?? '',
                  path: file.url ?? file.path,
                },
              }).then((res) => {
                if (res.status !== 201) {
                  dispatch(openSnackbar({ severity: 'warning', message: 'URL upload failed for content log' }));
                }
              })
            )
        );
      }

      return { id };
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        dispatch(openSnackbar({ severity: 'error', message: error.response.data.message }));
      }
    },
    onSuccess: async ({ id }: { id: string | undefined }) => {
      await invalidateLinkedServiceQueries();
      if (id) {
        markCreated(id);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const updateContentLogMutation = useMutation({
    mutationFn: async ({
      id,
      fields,
      uploadingFiles,
      uploadingUrls,
      removingFiles,
      removingUrls,
    }: UpdateContentLogWithFilesMutationProps) => {
      return updateContentLogWithFiles({
        id,
        fields,
        uploadingFiles,
        uploadingUrls,
        removingFiles,
        removingUrls,
      });
    },
    onSuccess: async () => {
      await invalidateLinkedServiceQueries();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const duplicateContentLogMutation = useMutation({
    mutationFn: async (id: string) => duplicateContentLog(id),
    onSuccess: async ({ id }: { id: string | undefined }) => {
      await invalidateLinkedServiceQueries();
      if (id) {
        markCreated(id);
      }
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  const deleteContentLogMutation = useMutation({
    mutationFn: async (id: string) => deleteContentLog(id),
    onSuccess: async () => {
      await invalidateLinkedServiceQueries();
      if (onSuccessCallback) {
        await onSuccessCallback();
      }
    },
  });

  return {
    type: TrackerDataType.CONTENT_LOG,
    newItemModalTitle: 'Add New Content Log',
    createNewItem: async (
      fields: ContentLog,
      files: Array<globalThis.File | (TrackerFile & { url?: string })>,
      successCallback: (arg0: { postId: string }) => void
    ) => {
      const result = await createContentLogMutation.mutateAsync({ fields, files });
      if (successCallback && result?.id) {
        successCallback({ postId: result.id });
      }
    },
    useListenOnCreation: (rowId: string) => ({ isNewlyAdded: isNewlyAdded(rowId) }),
    duplicateItem: async (id: string) => {
      await duplicateContentLogMutation.mutateAsync(id);
    },
    editItem: (val: any, index: string) => {
      if (!val) {
        return null;
      }

      switch (index) {
        case ContentLogKeys.type:
        case ContentLogKeys.expert:
        case ContentLogKeys.audience:
        case ContentLogKeys.campaign:
        case ContentLogKeys.lead:
        case ContentLogKeys.linkedService:
        case ContentLogKeys.file:
          return Array.isArray(val) ? [...val] : [val];
        case ContentLogKeys.entryDate:
        case ContentLogKeys.dueDate:
        case ContentLogKeys.topic:
        case ContentLogKeys.status:
        case ContentLogKeys.content:
        case ContentLogKeys.background:
        case ContentLogKeys.otherNotes:
        case ContentLogKeys.liveLink:
          return val;
        case ContentLogKeys.units:
          return Array.isArray(val) ? [...val] : [val];
        case ContentLogKeys.dept:
          return Array.isArray(val) ? val.map((dept) => dept?.name ?? dept) : [val?.name ?? val];
        default:
          return val;
      }
    },
    updateItemWithFile: async (
      { id, fields }: { id: string; fields: ContentLog },
      uploadingFiles: File[],
      uploadingUrls: any[],
      removingFiles: any[],
      removingUrls: any[]
    ) => {
      await updateContentLogMutation.mutateAsync({
        id,
        fields: fields as unknown as any,
        uploadingFiles,
        uploadingUrls,
        removingFiles,
        removingUrls,
      });
    },
    deleteItem: async (id: string) => {
      try {
        return await deleteContentLogMutation.mutateAsync(id);
      } catch (error) {
        console.error(`failed to delete content log - ${error}`);
      }
    },
    flattenForExport: (data: FlatContentLog[], dropdownTable: any) => {
      return data.map((row) => {
        const flatRow = JSON.parse(JSON.stringify(row.cells));
        flatRow[ContentLogKeys.entryDate] = dayjs(flatRow[ContentLogKeys.entryDate]).format('YYYY-MM-DD');
        flatRow[ContentLogKeys.dueDate] = flatRow[ContentLogKeys.dueDate]
          ? dayjs(flatRow[ContentLogKeys.dueDate]).format('YYYY-MM-DD')
          : '';
        flatRow[ContentLogKeys.expert] = mapExportValues(flatRow[ContentLogKeys.expert], (expert: any) =>
          getDropdownLabel(dropdownTable.expert, expert)
        );
        flatRow[ContentLogKeys.audience] = mapExportValues(flatRow[ContentLogKeys.audience], (audience: any) =>
          getDropdownLabel(dropdownTable.audience, audience)
        );
        flatRow[ContentLogKeys.lead] = mapExportValues(flatRow[ContentLogKeys.lead], (lead: any) =>
          getDropdownLabel(dropdownTable.lead, lead)
        );
        flatRow[ContentLogKeys.type] = (flatRow[ContentLogKeys.type] || []).map((type: any) => type.name).join(', ');
        flatRow[ContentLogKeys.campaign] = (flatRow[ContentLogKeys.campaign] || [])
          .map((campaign: any) => campaign.name)
          .join(', ');
        flatRow[ContentLogKeys.file] = (flatRow[ContentLogKeys.file] || []).map((file: any) => file.name).join(', ');
        flatRow[ContentLogKeys.liveLink] = flatRow[ContentLogKeys.liveLink]?.path ?? '';
        flatRow[ContentLogKeys.linkedService] = (flatRow[ContentLogKeys.linkedService] || [])
          .map((service: any) => service.name)
          .join(', ');
        return flatRow;
      });
    },
  };
};
